"use client"

import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    Download,
    Upload,
    Clock,
    Shield,
    Wallet,
    CheckCircle,
    XCircle,
    TrendingUp,
    TrendingDown,
    Users,
    Gift,
    DollarSign,
    ArrowRightCircle,
    RefreshCw,
    Search,
    ChevronDown,
    Sparkles,
    BarChart3,
    CreditCard
} from "lucide-react"

// MUI components
import {
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Tooltip,
    Chip
} from "@mui/material"

// Redux imports
import { exchangeActions } from "../../../../redux/actions"
import {
    userSelect,
    homeScreenStatSelect,
    allRemoteSettingsSelect,
    exchangeSelect,
} from "../../../../redux/selectors"
import { notify, money, moneyStatus, formatDateTime, capitalizeFirstLetter } from "../../../../utility/Helper1"
import LoadingIcon from "../../../../utility/customComponents/loader/LoadingIcon"
import PinAuth from "../../../../utility/customComponents/PinAuth"
import NoData from "../../../../utility/customComponents/NoData"
import WrapUpP2p from "../../p2p/layouts/WrapUpP2p"
import { useNavigate } from 'react-router-dom';

const WalletDashboard = () => {
    // Redux state
    const finance = useSelector(homeScreenStatSelect)
    const userData = useSelector(userSelect)
    const site_settings = useSelector(allRemoteSettingsSelect)
    const dispatch = useDispatch()
    const exchangeData = useSelector(exchangeSelect)
    const navigate = useNavigate();

    // Local state
    const [securityObj, setSecurityObj] = useState({})
    const [currencySelected, setCurrencySelected] = useState(finance?.[0])
    const [amount, setAmount] = useState("")
    const [withdrawalIsActive, setWithdrawalIsActive] = useState(false)
    const [selectedWallet, setSelectedWallet] = useState(1)
    const [activeTab, setActiveTab] = useState("withdraw") // 'withdraw', 'history', or 'stats'
    const [searchTerm, setSearchTerm] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [isOpen, setIsOpen] = useState(false);

    // Derived data
    const allWithdrawals = exchangeData?.filter(el => Number(el.asset) == 2) || []
    const statOnly = allWithdrawals?.map(el => ({ readOnlyTotalWithdrawn: el.activewallet2, status: el.completed, completed: el.completed }))
    const withdrawalHistory = exchangeData?.filter(el => Number(el.asset) === 2) || []

    // Filter history based on search term
    const filteredHistory = withdrawalHistory.filter(item =>
        item?.withdrawalColumn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.motherType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moneyStatus()[item?.completed]?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    const op_se = site_settings?.operational_settings || {}

    // Wallet source options
    const walletSources = [
        { title: "Main Balance", column: "balance", id: 1, useThis: true, icon: <Wallet className="h-5 w-5" /> },
        {
            title: "Commission",
            column: "commission",
            id: 2,
            useThis: Number(op_se?.useReferralBonus) === 1,
            icon: <Users className="h-5 w-5" />,
        },
    ]

    // Calculate summary statistics
    const totalWithdrawn = statOnly?.reduce((sum, item) => sum + Number.parseFloat(item.readOnlyTotalWithdrawn || 0), 0) || 0
    const pendingCount = statOnly?.filter(item => item.completed == 0).length || 0
    const completedCount = statOnly?.filter(item => item.completed == 1).length || 0
    const rejectedCount = statOnly?.filter(item => item.status == 2).length || 0

    // Handlers
    const onLoadPH = () => {
        setIsLoading(true)
        dispatch(exchangeActions.onGetExchange("p2p/load/all", response => {
            setIsLoading(false)
        }))
    }

    const processWithdrawal = (e) => {
        e.preventDefault()

        if (withdrawalIsActive) return

        if (isNaN(amount) || amount === 0) {
            notify("Please enter a valid amount", 0)
            return false
        }

        setWithdrawalIsActive(true)

        const walletChosenColumn = walletSources?.[selectedWallet - 1]?.["id"]

        dispatch(
            exchangeActions.onPostExchange(
                {
                    amount: amount,
                    ...(securityObj ?? {}),
                    currency: currencySelected?.type,
                },
                `p2p/withdrawal/new/${walletChosenColumn}`,
                (response) => {
                    notify(response.msg, response.code)
                    if (response.code === 1) {
                        setAmount("")
                    }
                    setWithdrawalIsActive(false)
                },
            ),
        )
    }

    const updateCurrencySelected = (typeOfSelected) => {
        const selected = finance?.filter((item) => item.type === typeOfSelected)?.[0] || {}
        setCurrencySelected(selected)
    }

    const changeSelectedWallet = (value) => {
        setSelectedWallet(value)
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 1:
                return "#10B981" // Green
            case 2:
                return "#EF4444" // Red
            case 0:
            default:
                return "#F59E0B" // Yellow
        }
    }

    const getStatusIcon = (status) => {
        switch (status) {
            case 1:
                return <CheckCircle className="h-5 w-5" style={{ color: "#10B981" }} />
            case 2:
                return <XCircle className="h-5 w-5" style={{ color: "#EF4444" }} />
            case 0:
            default:
                return <Clock className="h-5 w-5" style={{ color: "#F59E0B" }} />
        }
    }

    // Get wallet balance for selected currency
    const getWalletBalance = (walletId) => {
        if (!finance || !currencySelected) return 0

        const wallet = walletSources.find(w => w.id === parseInt(walletId))
        if (!wallet) return 0

        const curr = finance.find(f => f.type === currencySelected.type)
        return curr?.[wallet.column] || 0
    }

    // Effects
    useEffect(() => {
        onLoadPH()
    }, [])

    return (
        <WrapUpP2p>
            <div className="min-h-screen" style={{
                background: "linear-gradient(135deg, #050520 0%, #0F172A 100%)",
                color: "#F8FAFC"
            }}>
                <div className="max-w-7xl mx-auto px-4 py-8">
                    {/* Dashboard Header */}
                    <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center">
                        <div>
                            <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                                Wallet Withdrawal
                            </h1>
                            <p className="text-gray-300">Manage your funds and track transactions</p>
                        </div>
                        <div className="mt-4 md:mt-0">
                            <button
                                onClick={() => navigate('/withdrawal/history')}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 transition-all duration-300"
                            >
                                <ArrowRightCircle className="h-4 w-4" />
                                History
                            </button>
                        </div>
                    </div>

                    <div className="pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Status Distribution */}
                            <div className="rounded-xl border border-gray-700/50 bg-gray-800/20 p-5">
                                <h3 className="text-lg font-medium text-white mb-4">Status Distribution</h3>
                                <div className="flex items-center justify-center h-52">
                                    <div className="flex items-end h-full w-full max-w-md">
                                        {/* Completed Bar */}
                                        <div className="flex-1 flex flex-col items-center">
                                            <div
                                                className="w-16 rounded-t-lg bg-gradient-to-t from-green-500 to-green-400"
                                                style={{
                                                    height: `${completedCount ? (completedCount / (completedCount + pendingCount + rejectedCount) * 100) : 0}%`,
                                                    minHeight: completedCount ? '20px' : '0px'
                                                }}
                                            ></div>
                                            <div className="mt-2 text-sm font-medium text-green-400">{completedCount}</div>
                                            <div className="mt-1 text-xs text-gray-400">Completed</div>
                                        </div>

                                        {/* Pending Bar */}
                                        <div className="flex-1 flex flex-col items-center">
                                            <div
                                                className="w-16 rounded-t-lg bg-gradient-to-t from-amber-500 to-amber-400"
                                                style={{
                                                    height: `${pendingCount ? (pendingCount / (completedCount + pendingCount + rejectedCount) * 100) : 0}%`,
                                                    minHeight: pendingCount ? '20px' : '0px'
                                                }}
                                            ></div>
                                            <div className="mt-2 text-sm font-medium text-amber-400">{pendingCount}</div>
                                            <div className="mt-1 text-xs text-gray-400">Pending</div>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/* Monthly Activity */}
                            <div className="rounded-xl border border-gray-700/50 bg-gray-800/20 p-5">
                                <h3 className="text-lg font-medium text-white mb-4">Monthly Activity</h3>
                                <div className="flex items-center justify-center h-52">
                                    <div className="relative w-full h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="w-24 h-24 mx-auto rounded-full flex items-center justify-center"
                                                style={{
                                                    background: "conic-gradient(#10B981 0%, #10B981 60%, #F59E0B 60%, #F59E0B 85%, #EF4444 85%, #EF4444 100%)",
                                                    boxShadow: "0 0 30px rgba(16, 185, 129, 0.3)"
                                                }}
                                            >
                                                <div className="w-16 h-16 rounded-full bg-gray-800/80 flex items-center justify-center">
                                                    <span className="text-white font-bold">{allWithdrawals?.length || 0}</span>
                                                </div>
                                            </div>
                                            <div className="mt-4 grid grid-cols-3 gap-2">
                                                <div className="flex items-center justify-center">
                                                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                                                    <span className="text-xs text-gray-400">60%</span>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <div className="w-3 h-3 rounded-full bg-amber-500 mr-2"></div>
                                                    <span className="text-xs text-gray-400">25%</span>
                                                </div>
                                                <div className="flex items-center justify-center">
                                                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                                                    <span className="text-xs text-gray-400">15%</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="rounded-2xl overflow-hidden" style={{
                        background: "linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)"
                    }}>


                        <div className="p-6">

                            <form onSubmit={processWithdrawal} className="space-y-6">
                                {finance?.length > 1 && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Select Currency</label>
                                        <div className="relative">
                                            <select
                                                onChange={(e) => updateCurrencySelected(e.target.value)}
                                                className="block w-full pl-4 pr-10 py-3 text-white border-gray-700 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-xl appearance-none bg-gray-800/50 border"
                                            >
                                                {finance?.map((item, index) => (
                                                    <option key={index} value={item.type}>
                                                        {item?.abbr} - {item?.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                                                <ChevronDown className="h-5 w-5" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <h3 className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                                        Source Wallet
                                    </h3>

                                    <div className="grid grid-cols-1 gap-3">
                                        {walletSources?.map((item, index) => {
                                            if (!item.useThis) return null;

                                            const balance = getWalletBalance(item.id);
                                            const showBalance = balance !== "" ? money(balance, currencySelected?.type) : "0";
                                            const isSelected = selectedWallet === item.id;

                                            return (
                                                <div
                                                    key={index}
                                                    onClick={() => changeSelectedWallet(item.id)}
                                                    className={`relative overflow-hidden rounded-xl transition-all duration-300 cursor-pointer ${isSelected
                                                        ? 'border-transparent shadow-[0_0_20px_rgba(6,182,212,0.6)]'
                                                        : 'border border-gray-700/50 hover:border-cyan-500/30 hover:shadow-[0_0_10px_rgba(6,182,212,0.3)]'
                                                        }`}
                                                >
                                                    {/* Background with gradient effect */}
                                                    <div className={`absolute inset-0 ${isSelected
                                                        ? 'bg-gradient-to-r from-cyan-900/40 to-purple-900/40 backdrop-blur-sm'
                                                        : 'bg-gray-800/50'
                                                        }`} />

                                                    {/* Selected indicator */}
                                                    {isSelected && (
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400 to-purple-500" />
                                                    )}

                                                    <div className="relative p-4 flex items-center justify-between">
                                                        <div className="flex items-center space-x-3">
                                                            {/* Wallet icon with glow effect */}
                                                            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${isSelected
                                                                ? 'bg-gradient-to-br from-cyan-500/20 to-purple-500/20 shadow-[0_0_10px_rgba(6,182,212,0.4)]'
                                                                : 'bg-gray-700/50'
                                                                }`}>
                                                                <svg className={`w-5 h-5 ${isSelected ? 'text-cyan-400' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                                </svg>
                                                            </div>

                                                            <div>
                                                                <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>
                                                                    {item.title}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <div className={`text-right ${isSelected ? 'text-cyan-400' : 'text-gray-400'}`}>
                                                            <p className="text-sm font-bold">{showBalance}</p>
                                                            <p className="text-xs opacity-70">{isSelected ? 'Selected' : ''}</p>
                                                        </div>
                                                    </div>

                                                    {/* Glow border for selected item */}
                                                    {isSelected && (
                                                        <div className="absolute inset-0 rounded-xl border border-cyan-500/50 pointer-events-none" />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <h3 className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                                        Amount to Withdraw
                                    </h3>

                                    <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-gray-900/80 to-gray-800/80 p-0.5 shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                                        {/* Animated gradient border */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-purple-500/30 rounded-xl opacity-50"></div>

                                        <div className="relative bg-gray-900/90 rounded-[10px] p-4">
                                            <div className="flex items-end justify-between">
                                                <div className="flex-1">
                                                    <input
                                                        type="text"
                                                        value={amount}
                                                        onChange={(e) => setAmount(e.target.value)}
                                                        className="w-full bg-transparent border-none text-2xl md:text-3xl font-bold text-white focus:outline-none focus:ring-0 placeholder-gray-600 p-0"
                                                        placeholder="0.00"
                                                    />
                                                </div>

                                                <div className="flex items-center ml-3">
                                                    <span className="text-lg font-medium text-cyan-400">{currencySelected?.abbr}</span>
                                                </div>
                                            </div>

                                            {/* Glowing line separator */}
                                            <div className="h-px w-full bg-gradient-to-r from-cyan-500/10 via-cyan-500/50 to-purple-500/10 my-4 shadow-[0_0_5px_rgba(6,182,212,0.5)]"></div>

                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center space-x-2">
                                                    <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse"></div>
                                                    <span className="text-xs text-gray-400">Available: {money(getWalletBalance(selectedWallet), currencySelected?.type)}</span>
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setAmount(getWalletBalance(selectedWallet))}
                                                    className="px-3 py-1 text-xs font-medium rounded-lg bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 hover:text-white transition-all duration-300 hover:shadow-[0_0_10px_rgba(6,182,212,0.5)] border border-cyan-500/30"
                                                >
                                                    MAX
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Optional: Add visual indicators for amount */}
                                    <div className="h-1 w-full bg-gray-800 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 transition-all duration-300"
                                            style={{
                                                width: `${Math.min(
                                                    (parseFloat(amount || '0') / parseFloat(getWalletBalance(selectedWallet) || '1')) * 100,
                                                    100
                                                )}%`
                                            }}
                                        ></div>
                                    </div>
                                </div>


                                <PinAuth resultingObjMethod={setSecurityObj} userData={userData} />

                                <button
                                    type="submit"
                                    disabled={withdrawalIsActive}
                                    className={`w-full flex justify-center py-3 px-4 rounded-xl text-sm font-medium text-white relative overflow-hidden ${withdrawalIsActive ? "opacity-70 cursor-not-allowed" : ""
                                        }`}
                                    style={{
                                        background: withdrawalIsActive
                                            ? "linear-gradient(90deg, #475569, #334155)"
                                            : "linear-gradient(90deg, #3B82F6, #8B5CF6)"
                                    }}
                                >
                                    <span className="relative z-10 flex items-center">
                                        {withdrawalIsActive ? (
                                            <>
                                                <span>Processing</span>
                                                <LoadingIcon color={"#fff"} active={withdrawalIsActive} />
                                            </>
                                        ) : (
                                            <>
                                                <span>Request Withdrawal</span>
                                                <ArrowRightCircle className="ml-2 h-4 w-4" />
                                            </>
                                        )}
                                    </span>
                                </button>
                            </form>
                        </div>

                    </div>
                </div>
            </div>
        </WrapUpP2p>)
}

export default WalletDashboard